<script setup lang="ts">
import { computed, getCurrentInstance, ref, unref } from 'vue';
import { useFetchVulnerability } from '@/_core/entities/vulnerability/api/useFetchVulnerability';
import { useLlmFeedbackFlow } from '@/_core/entities/llm-feedback/api/useLlmFeedbackFlow';
import {
  mockVulnerabilityTechnicalDetails,
  mockVulnerabilityHowToFix,
} from '@/_core/entities/vulnerability/models/mock.data';
import { useFetchAppVersionReport } from '@/_core/entities/report/api/useFetchAppVersionReport';
import { useFetchSingleScanReport } from '@/_core/entities/report/api/useFetchSingleScanReport';
import { useFetchSingleScanFolders } from '@/_core/entities/report/api/useFetchSingleScanFolders';
import { useFetchAppVersionFolders } from '@/_core/entities/report/api/useFetchAppVersionFolders';
import {
  useCreateFolder,
  useMoveVulnerabilityToFolder,
} from '@/_core/entities/vulnerability/api/useCreateFolderAndMove';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import { useReportPayload } from '@/_core/entities/report/utils/report.utils';
import type {
  TPercentageByType,
  TSingleScanFoldersPayload,
  TAppVersionFoldersPayload,
  TFolder,
} from '@/_core/entities/report/models/types';
import type {
  TCreateFolderPayload,
  TFetchVulnerabilityPayload,
} from '@/_core/entities/vulnerability/models/types';
import { buildMoveFolderPayload } from '@/_core/entities/vulnerability/utils/buildMovePayload';
import type { TLlmFeedbackContentType } from '@/_core/entities/llm-feedback/models/types';
import { FEEDBACK_TYPE } from '@/_core/entities/llm-feedback/models/types';
import VulnerabilityBreadcrumbs from '@/_core/entities/vulnerability/ui/VulnerabilityBreadcrumbs/VulnerabilityBreadcrumbs.vue';
import VulnerabilityGeneralDetails from '@/_core/entities/vulnerability/ui/VulnerabilityGeneralDetails/VulnerabilityGeneralDetails.vue';
import ReportSection from '@/_core/entities/report/ui/ReportSection/ReportSection.vue';
import ReportSummary from '@/_core/entities/report/ui/ReportSummary/ReportSummary.vue';
import Accordion from '@/_core/shared/ui/Accordion/Accordion.vue';
import VulnerabilityEvidenceDetails from '@/_core/entities/vulnerability/ui/VulnerabilityEvidenceDetails/VulnerabilityEvidenceDetails.vue';
import VulnerabilityTechnicalDetails from '@/_core/entities/vulnerability/ui/VulnerabilityTechnicalDetails/VulnerabilityTechnicalDetails.vue';
import ReportVulnerabilityPageSkeleton from '@/_core/pages/report/ReportVulnerabilityPageSkeleton.vue';
import LlmFeedbackRating from '@/_core/shared/ui/LlmFeedbackRating/LlmFeedbackRating.vue';
import LlmFeedbackCommentModal from '@/_core/shared/ui/LlmFeedbackCommentModal/LlmFeedbackCommentModal.vue';
import TrialOverlay from '@/_core/entities/vulnerability/ui/TrialOverlay/TrialOverlay.vue';
import ReportComplianceRequirements from '@/_core/widgets/ReportComplianceRequirements/ReportComplianceRequirements.vue';
import { useFetchComplianceMappings } from '@/_core/entities/report/api/useFetchComplianceMappings';
import type { TComplianceMappingsPayload } from '@/_core/entities/report/models/types';

const instance = getCurrentInstance();
const routeRef = computed(() => instance?.proxy.$route);

const reportType = computed(() => (unref(routeRef)?.params?.jobId ? 'single-scan' : 'app-version'));

const singleScanPayloadRef = useReportPayload('single-scan', routeRef);
const appVersionPayloadRef = useReportPayload('app-version', routeRef);

const singleScanQuery = useFetchSingleScanReport(singleScanPayloadRef);
const appVersionQuery = useFetchAppVersionReport(appVersionPayloadRef);

const reportDetailsQuery = reportType.value === 'single-scan' ? singleScanQuery : appVersionQuery;

const {
  data: reportData,
  isLoading: isReportLoading,
  isFetching: isReportFetching,
} = reportDetailsQuery;

const vulnerabilityPayloadRef = computed<TFetchVulnerabilityPayload | null>(() => {
  const uid = unref(routeRef)?.params?.uid;
  if (!uid) return null;
  return { uid };
});

const { data: vulnerabilityData, isFetching: isFetchingVulnerability } =
  useFetchVulnerability(vulnerabilityPayloadRef);

const complianceMappingsPayload = computed<TComplianceMappingsPayload | null>(() => {
  const uid = unref(routeRef)?.params?.uid;
  const integrationId = unref(routeRef)?.params?.integrationId;
  if (!uid || !integrationId) return null;
  return { vulnerabilityUid: uid, integrationId };
});

const { data: complianceMappingsData, isLoading: isComplianceMappingsLoading } =
  useFetchComplianceMappings(complianceMappingsPayload);

function getComplianceQuery() {
  const query = unref(routeRef)?.query;
  const requirementId = Array.isArray(query?.complianceRequirementId)
    ? query?.complianceRequirementId[0]
    : query?.complianceRequirementId;
  const itemId = Array.isArray(query?.complianceRequirementItemId)
    ? query?.complianceRequirementItemId[0]
    : query?.complianceRequirementItemId;
  return {
    complianceRequirementId: requirementId || undefined,
    complianceRequirementItemId: itemId || undefined,
  };
}

const singleScanFoldersPayloadRef = computed<TSingleScanFoldersPayload | null>(() => {
  const params = unref(routeRef)?.params;
  if (!params?.jobId) return null;
  return { jobId: params.jobId, ...getComplianceQuery() };
});

const appVersionFoldersPayloadRef = computed<TAppVersionFoldersPayload | null>(() => {
  const params = unref(routeRef)?.params;
  if (!params?.integrationId || !params?.versionId) return null;
  return {
    integrationId: params.integrationId,
    versionId: params.versionId,
    ...getComplianceQuery(),
  };
});

const singleScanFoldersQuery = useFetchSingleScanFolders(singleScanFoldersPayloadRef);
const appVersionFoldersQuery = useFetchAppVersionFolders(appVersionFoldersPayloadRef);

const foldersQuery =
  reportType.value === 'single-scan' ? singleScanFoldersQuery : appVersionFoldersQuery;

const { data: foldersData, isLoading: areFoldersLoading, isError: hasFoldersError } = foldersQuery;

const toast = useToast();

const { mutateAsync: createFolder } = useCreateFolder();
const { mutateAsync: moveVulnerability } = useMoveVulnerabilityToFolder();

const isCreateFolderModalOpen = ref(false);
const isCreateFolderLoading = ref(false);
const isMovingVulnToFolder = ref(false);

function openCreateFolderModal() {
  isCreateFolderModalOpen.value = true;
}

function closeCreateFolderModal() {
  isCreateFolderModalOpen.value = false;
}

async function handleMoveToFolder(folder: TFolder) {
  const vulnerability = vulnerabilityData.value;
  const params = unref(routeRef)?.params;
  const payload =
    vulnerability && params
      ? buildMoveFolderPayload(params, vulnerability.uid, folder.holderId, folder.name)
      : null;

  if (!payload) return;

  isMovingVulnToFolder.value = true;

  try {
    await moveVulnerability(payload);
    toast.success({ message: `Vulnerability moved to "${folder.name}".` });
  } catch {
    toast.error({ message: 'Failed to move vulnerability' });
  } finally {
    isMovingVulnToFolder.value = false;
  }
}

async function handleCreateFolder(folderName: string) {
  const vulnerability = vulnerabilityData.value;
  const params = unref(routeRef)?.params;

  if (!vulnerability || !params) return;

  const holderId = params.jobId ?? params.integrationId;
  const payload = holderId
    ? buildMoveFolderPayload(params, vulnerability.uid, holderId, folderName)
    : null;

  if (!payload) return;

  let newFolderPayload: TCreateFolderPayload | null = null;
  if (params.jobId) {
    newFolderPayload = { jobId: params.jobId, name: folderName };
  } else if (params.integrationId) {
    newFolderPayload = { integrationId: params.integrationId, name: folderName };
  }

  if (!newFolderPayload) {
    toast.error({ message: 'Failed to create folder. Parameters are missing.' });
    return;
  }

  isCreateFolderLoading.value = true;

  try {
    await createFolder(newFolderPayload);
    await moveVulnerability(payload);
    closeCreateFolderModal();
    toast.success({ message: `Vulnerability moved to "${folderName}".` });
  } catch {
    toast.error({ message: 'Failed to create folder' });
  } finally {
    isCreateFolderLoading.value = false;
  }
}

const categoryPercentageMap = computed(() =>
  (reportData.value?.statistics.percentageByType ?? []).reduce<Record<string, TPercentageByType>>(
    (acc, item) => {
      acc[item.id] = item;
      return acc;
    },
    {}
  )
);

const isSampleReport = computed(
  () => reportData.value?.type === 'SAMPLE' || reportData.value?.type === 'VERSION_SAMPLE'
);

const isTrialWithLlm = computed(
  () => vulnerabilityData.value?.isTrialIntegration && vulnerabilityData.value?.hasLlmDescription
);

const shouldUseMockLlmData = computed(() => isSampleReport.value || !!isTrialWithLlm.value);

const technicalDetails = computed(() => {
  if (shouldUseMockLlmData.value) return mockVulnerabilityTechnicalDetails;
  return vulnerabilityData.value?.llmDescription?.technicalDetails ?? null;
});

const howToFixContent = computed(() => {
  if (shouldUseMockLlmData.value) return mockVulnerabilityHowToFix;

  const remediationInfo = vulnerabilityData.value?.llmDescription?.remediation;

  if (!remediationInfo || typeof remediationInfo !== 'string') return null;

  return remediationInfo;
});

const hasSummaryContent = computed(() => Boolean(technicalDetails.value || howToFixContent.value));
const canShowFeedback = computed(
  () =>
    Boolean(vulnerabilityData.value) &&
    (!!vulnerabilityData.value?.llmDescription || shouldUseMockLlmData.value)
);

function getServerRating(contentType: TLlmFeedbackContentType) {
  const data = vulnerabilityData.value;
  if (!data) return null;
  if (contentType === FEEDBACK_TYPE.VULN_TECHNICAL_DETAILS) {
    return data.technicalDetailsFeedback ?? null;
  }
  return data.remediationFeedback ?? null;
}

const {
  isCommentModalOpen,
  closeCommentModal,
  handleRate,
  handleCommentSubmit,
  getEffectiveRating: getEffectiveVulnRating,
  isPending: isFeedbackPending,
} = useLlmFeedbackFlow({
  getContentId: () => vulnerabilityData.value?.uid,
  getQueryKey: () => ['vulnerability', vulnerabilityData.value?.uid ?? ''],
  getServerRating,
  isContentAvailable: () =>
    !!vulnerabilityData.value &&
    (!!vulnerabilityData.value?.llmDescription || !!shouldUseMockLlmData.value),
  skipSubmit: () => shouldUseMockLlmData.value,
});
</script>

<template>
  <div class="tw:py-3 tw:lg:px-4">
    <ReportVulnerabilityPageSkeleton v-if="isReportLoading || isReportFetching" />

    <div v-else class="tw:flex tw:flex-col tw:gap-2 tw:lg:gap-6">
      <VulnerabilityBreadcrumbs
        v-if="vulnerabilityData && reportData"
        :vulnerability-data="vulnerabilityData"
        :report-data="reportData"
        :job-id="routeRef?.params?.jobId"
        :integration-id="routeRef?.params?.integrationId"
        :branch-name="routeRef?.params?.branchName"
        :version-id="routeRef?.params?.versionId"
      />

      <VulnerabilityGeneralDetails
        v-if="vulnerabilityData && reportData"
        :vulnerability="vulnerabilityData"
        :categoryPercentageMap="categoryPercentageMap"
        :folders="foldersData ?? []"
        :areFoldersLoading="areFoldersLoading"
        :hasFoldersError="hasFoldersError"
        :is-create-folder-modal-open="isCreateFolderModalOpen"
        :is-create-folder-loading="isCreateFolderLoading"
        :isMovingVulnToFolder="isMovingVulnToFolder"
        :isFetchingVulnerability="isFetchingVulnerability"
        @open-create-folder-modal="openCreateFolderModal"
        @close-create-folder-modal="closeCreateFolderModal"
        @create-folder="handleCreateFolder"
        @move="handleMoveToFolder"
      />

      <div
        v-if="technicalDetails || howToFixContent"
        class="tw:flex tw:flex-col tw:gap-2 tw:xl:flex-row tw:xl:gap-4 tw:xl:items-start"
      >
        <TrialOverlay title="Technical Details and How to Fix" :isContentUnlocked="!isTrialWithLlm">
          <ReportSection classNames="tw:flex-1">
            <Accordion>
              <div class="tw:flex tw:gap-4 tw:lg:gap-12">
                <div v-if="technicalDetails" class="tw:flex-1">
                  <ReportSummary title="Technical Details">
                    <VulnerabilityTechnicalDetails :technical-details="technicalDetails" />
                  </ReportSummary>
                  <div v-if="canShowFeedback" class="tw:mt-4 tw:flex tw:justify-end">
                    <LlmFeedbackRating
                      :rating="getEffectiveVulnRating(FEEDBACK_TYPE.VULN_TECHNICAL_DETAILS)"
                      :isPending="isFeedbackPending"
                      @rate="handleRate(FEEDBACK_TYPE.VULN_TECHNICAL_DETAILS, $event)"
                    />
                  </div>
                </div>
                <div v-if="howToFixContent" class="tw:flex-1 tw:hidden tw:lg:block">
                  <ReportSummary title="How to Fix" :content="howToFixContent" />
                  <div v-if="canShowFeedback" class="tw:mt-4 tw:flex tw:justify-end">
                    <LlmFeedbackRating
                      :rating="getEffectiveVulnRating(FEEDBACK_TYPE.VULN_REMEDIATION)"
                      :isPending="isFeedbackPending"
                      @rate="handleRate(FEEDBACK_TYPE.VULN_REMEDIATION, $event)"
                    />
                  </div>
                </div>
              </div>
            </Accordion>
          </ReportSection>
        </TrialOverlay>
      </div>

      <div v-if="howToFixContent" class="tw:mt-2 tw:lg:mt-4 tw:block tw:lg:hidden">
        <TrialOverlay title="How to Fix" :isContentUnlocked="!isTrialWithLlm">
          <ReportSection>
            <Accordion>
              <ReportSummary title="How to Fix" :content="howToFixContent" />
              <div v-if="canShowFeedback" class="tw:mt-4 tw:flex tw:justify-end">
                <LlmFeedbackRating
                  :rating="getEffectiveVulnRating(FEEDBACK_TYPE.VULN_REMEDIATION)"
                  :isPending="isFeedbackPending"
                  @rate="handleRate(FEEDBACK_TYPE.VULN_REMEDIATION, $event)"
                />
              </div>
            </Accordion>
          </ReportSection>
        </TrialOverlay>
      </div>

      <ReportSection v-if="canShowFeedback && !hasSummaryContent" classNames="tw:mt-2 tw:lg:mt-4">
        <div class="tw:flex tw:items-center tw:justify-end">
          <LlmFeedbackRating
            :rating="getEffectiveVulnRating(FEEDBACK_TYPE.VULN_TECHNICAL_DETAILS)"
            :isPending="isFeedbackPending"
            @rate="handleRate(FEEDBACK_TYPE.VULN_TECHNICAL_DETAILS, $event)"
          />
        </div>
      </ReportSection>

      <LlmFeedbackCommentModal
        :isOpen="isCommentModalOpen"
        :isPending="isFeedbackPending"
        @submit="handleCommentSubmit"
        @close="closeCommentModal"
      />

      <ReportSection v-if="complianceMappingsPayload">
        <ReportComplianceRequirements
          mode="mappings"
          :mappings="complianceMappingsData"
          :is-loading="isComplianceMappingsLoading"
          :integration-id="routeRef?.params?.integrationId"
        />
      </ReportSection>

      <!-- TODO: expand/collapse all sections -->
      <VulnerabilityEvidenceDetails
        v-if="vulnerabilityData"
        :vulnerability="vulnerabilityData"
        :report-data="reportData"
      />
    </div>
  </div>
</template>
